import React, { useContext, useEffect, useState } from "react";
import { usePopupLock } from "../../context/PopupLockContext";
import { UserContext } from "../../context/UserContext";
import UpdateProfileModal from "../../component/Navbar/UpdateProfileModal";

export default function CallPopup({ isOpen, onClose }) {
  const { user } = useContext(UserContext);
  const { openLock, closeLock } = usePopupLock();

  console.log("🔥 USER OBJECT:", user);

  const identifier = user?.identifier || "";
  const isEmailLogin = identifier.includes("@");
  const isLoggedIn = !!user;

  const resolvedPhone =
    user?.phoneNo ||
    user?.phone ||
    user?.phoneNumber ||
    (!isEmailLogin ? user?.identifier : "") ||
    "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNo: "",
  });

  const [loading, setLoading] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  
  // ✅ Call state
  const [isCalling, setIsCalling] = useState(false);

  // ✅ API Base URL from env
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  /* ================= RESET + OPEN ================= */
  useEffect(() => {
    if (!isOpen) {
      setForm({ name: "", email: "", phoneNo: "" });
      setIsAutoSubmitted(false);
      closeLock("call");
      return;
    }

    setForm({
      name: user?.name || user?.fullName || user?.userName || "",
      email: isEmailLogin ? user?.identifier || "" : user?.email || "",
      phoneNo: resolvedPhone,
    });

    openLock("call");
  }, [isOpen, user]);

  /* ================= AUTO SUBMIT ================= */ 
  useEffect(() => {
    if (!isLoggedIn || isAutoSubmitted || !isOpen) return;

    const autoSubmit = async () => {
      try {
        const payload = {
          id: 0,
          userId: user?.userId || 0,
          name: user?.name || user?.fullName || user?.userName || "",
          email: isEmailLogin
            ? user?.identifier || ""
            : user?.email || "",
          phoneNo: resolvedPhone,
          dateForCall: new Date().toISOString(),
          status: true,
        };

        console.log("📤 AUTO PAYLOAD:", payload);

        const response = await fetch(
          `${API_BASE_URL}Customer/InsertCallUs`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();

        console.log("✅ AUTO RESPONSE:", data);

        if (response.ok) {
          console.log("🎉 AUTO SUBMIT SUCCESS");
        }

        setIsAutoSubmitted(true);
      } catch (err) {
        console.log("❌ AUTO ERROR:", err);
      }
    };

    autoSubmit();
  }, [isOpen, isLoggedIn, isAutoSubmitted, user, resolvedPhone, API_BASE_URL]);

  if (!isOpen) return null;

  /* ================= CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* ================= CLOSE ================= */
  const handleClose = () => {
    closeLock("call");
    onClose();
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (!form.phoneNo) return alert("Phone is required");
    if (!form.name) return alert("Name is required");

    try {
      setLoading(true);

      const payload = {
        id: 0,
        userId: 0,
        name: form.name,
        email: form.email,
        phoneNo: form.phoneNo,
        dateForCall: new Date().toISOString(),
        status: true,
      };

      console.log("📤 MANUAL PAYLOAD:", payload);

      const res = await fetch(
        `${API_BASE_URL}Customer/InsertCallUs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      console.log("📡 RESPONSE:", data);

      if (res.ok) {
        alert("Request submitted successfully ✅");
        handleClose();
      } else {
        alert("Something went wrong ❌");
      }
    } catch (err) {
      console.log(err);
      alert("Something went wrong ❌");
    } finally {
      setLoading(false);
    }
  };

  /* ================= CALL FUNCTION ================= */
  const handleCallNow = () => {
    setIsCalling(true);
    
    // ✅ Phone number to call
    const phoneNumber = "918178399885"; // Without + sign for tel: protocol
    
    // ✅ Check if device supports calling
    if (window.location.protocol === 'https:' || window.location.protocol === 'http:') {
      // For mobile devices - opens dialer
      window.location.href = `tel:+${phoneNumber}`;
    } else {
      // For desktop - show alert
      alert(`Please call us at +91-81783 99885`);
    }
    
    // Reset calling state after 2 seconds
    setTimeout(() => {
      setIsCalling(false);
    }, 2000);
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* BACKDROP */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />

      {/* MODAL */}
      <div className="relative bg-white rounded-2xl shadow-xl p-6 w-[90%] max-w-md z-10">

        {/* LOGGED IN UI */}
        {isLoggedIn ? (
          <div className="text-center py-5 space-y-4">
            <h3 className="text-lg font-bold text-red-600">
              THANK YOU FOR CHOOSING US.
            </h3>

            <p className="text-gray-700 font-medium">
              SOON OUR REPRESENTATIVE WILL CONNECT YOU.
            </p>

            {/* ✅ Divider */}
            <div className="flex items-center gap-3 my-3">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="text-gray-400 text-sm font-medium">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* ✅ Call Now Button */}
            <div className="space-y-2">
              <p className="text-sm text-gray-500">
                Need immediate assistance?
              </p>
              
              <button
                onClick={handleCallNow}
                disabled={isCalling}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isCalling ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connecting...
                  </>
                ) : (
                  <>
                    {/* Phone Icon */}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call Now: +91-81783 99885
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400">
                * Standard call charges may apply
              </p>
            </div>

            {/* Emergency text */}
            <p className="text-xs text-gray-400 mt-2">
              Available: Mon-Sat, 9:00 AM - 9:00 PM
            </p>
          </div>
        ) : (
          /* GUEST FORM */
          <div className="space-y-3">
            <input
              type="text"
              name="name"
              placeholder="Enter Name"
              value={form.name}
              onChange={handleChange}
              className="w-full border p-2 rounded-lg"
            />
            
            <input
              type="email"
              name="email"
              placeholder="Enter Email"
              value={form.email}
              onChange={handleChange}
              className="w-full border p-2 rounded-lg"
            />

            <input
              type="tel"
              name="phoneNo"
              placeholder="Enter Phone Number"
              value={form.phoneNo}
              maxLength={10}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                setForm((prev) => ({ ...prev, phoneNo: value }));
              }}
              className="w-full border p-2 rounded-lg"
            />
          </div>
        )}

        {/* BUTTONS */}
        <div className="flex gap-3 mt-5">
          {!isLoggedIn && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 text-white py-2.5 rounded-xl"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          )}

          <button
            onClick={handleClose}
            className={`${
              isLoggedIn ? 'flex-1' : 'flex-1'
            } border py-2.5 rounded-xl hover:bg-gray-50 transition-colors`}
          >
            {isLoggedIn ? "Close" : "Cancel"}
          </button>
        </div>
      </div>

      {/* UPDATE MODAL */}
      <UpdateProfileModal
        show={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        onSuccess={(data) => {
          console.log("PROFILE UPDATED:", data);
        }}
      />
    </div>
  );
}